import { useCallback, useEffect, useRef, useState } from 'react'

import { handleException } from 'clients/exception-handling'
import type { TrendDetectionService } from 'features/trend-detection/service/service'
import type { Chart, TrendFeedUpdateItem } from 'features/trend-detection/types'

export interface UseFeedUpdateChartsResult {
  updateTimeseriesCharts: Record<string, Chart | null>
  updateTimeseriesLoadingMap: Record<string, boolean>
  updateProjectTotalChartMap: Record<string, Chart>
}

export function useFeedUpdateCharts(
  service: TrendDetectionService,
  projectId: string | null,
  updatesItems: TrendFeedUpdateItem[],
): UseFeedUpdateChartsResult {
  const itemsRef = useRef(updatesItems)
  itemsRef.current = updatesItems

  const runForUpdate = useCallback((updateUuid: string): string | null => {
    // Use the update's own run_uuid (not the parent item's, which describes the
    // source issue's originating run) so the chart window matches the update
    // detail page's chart.
    return (
      itemsRef.current.find((it) => it.update?.uuid === updateUuid)?.update
        ?.run_uuid ?? null
    )
  }, [])

  const [updateTimeseriesCharts, setUpdateTimeseriesCharts] = useState<
    Record<string, Chart | null>
  >({})
  const [updateTimeseriesLoadingMap, setUpdateTimeseriesLoadingMap] = useState<
    Record<string, boolean>
  >({})
  const [updateProjectTotalChartMap, setUpdateProjectTotalChartMap] = useState<
    Record<string, Chart>
  >({})

  const chartsRef = useRef(updateTimeseriesCharts)
  chartsRef.current = updateTimeseriesCharts
  const loadingRef = useRef(updateTimeseriesLoadingMap)
  loadingRef.current = updateTimeseriesLoadingMap

  const fetchTimeseriesForUpdate = useCallback(
    (updateUuid: string, sourceIssueUuid: string): void => {
      const runUuid = runForUpdate(updateUuid)
      if (!projectId || !runUuid) return
      setUpdateTimeseriesLoadingMap((prev) => ({
        ...prev,
        [updateUuid]: true,
      }))
      service
        .fetchIssueTimeseriesChart(projectId, runUuid, sourceIssueUuid)
        .then((response) => {
          if (response != null) {
            setUpdateTimeseriesCharts((prev) => ({
              ...prev,
              [updateUuid]: response.issue_chart,
            }))
            if (response.project_total_chart) {
              setUpdateProjectTotalChartMap((prev) => ({
                ...prev,
                [updateUuid]: response.project_total_chart,
              }))
            }
          }
        })
        .catch((cause) => {
          handleException(cause, {
            operation: 'agent.feed.update-charts.load',
            disposition: 'failed',
          })
        })
        .finally(() => {
          setUpdateTimeseriesLoadingMap((prev) => ({
            ...prev,
            [updateUuid]: false,
          }))
        })
    },
    [projectId, runForUpdate, service],
  )

  useEffect(() => {
    if (!projectId || updatesItems.length === 0) return
    for (const it of updatesItems) {
      // Skip freshly-tracked items that don't have an update row yet — there's
      // nothing to chart until a scheduled run produces one.
      if (!it.update) continue
      const updateUuid = it.update.uuid
      const hasChart = (chartsRef.current[updateUuid]?.data?.length ?? 0) > 0
      if (hasChart || loadingRef.current[updateUuid]) continue
      fetchTimeseriesForUpdate(updateUuid, it.update.source_issue_uuid)
    }
  }, [updatesItems, projectId, fetchTimeseriesForUpdate])

  return {
    updateTimeseriesCharts,
    updateTimeseriesLoadingMap,
    updateProjectTotalChartMap,
  }
}
